import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import { task } from 'ember-concurrency';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { timeout } from 'lumen/utils/waiters';

interface PropertyOption extends Option {
  id: string;
}

interface AutocompleteResult {
  brand_code: string;
  description: string;
  hotel_id: string | undefined;
  market_id: string | undefined;
  name: string;
  products: string[] | undefined;
  subscription_type: string | undefined;
}

interface AutocompleteMeta {
  results_count: number;
  results_per_page: number;
}

interface PropertySelectSignature {
  Args: {
    isSecondary?: boolean;
    onChange: (selectedOption: PropertyOption | null) => void;
    value: PropertyOption | null;
  };
}

export default class PropertySelect extends Component<PropertySelectSignature> {
  @service('ajax') declare ajaxService: AjaxService;
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];

  @tracked options: PropertyOption[] = [];
  @tracked page = 0;
  @tracked searchQuery = '';
  @tracked totalPages = 0;

  readonly allPropertiesOption: PropertyOption;
  fetchPropertiesTask = task(
    { restartable: true },
    async (): Promise<PropertyOption[]> => {
      try {
        const { meta, results } = await this.ajaxService.request<{
          meta: AutocompleteMeta;
          results: AutocompleteResult[];
        }>('autocomplete/subscription_new_user/', {
          data: { page: this.page, q: this.searchQuery },
          method: 'GET',
        });

        this.totalPages = Math.ceil(meta.results_count / meta.results_per_page);

        return this.hotelAndMarketService
          .formatAutocompleteResults(results)
          .filter((result) => result.hotel_id !== undefined)
          .map((result) => ({
            badge: result.subscription_type,
            id: result.hotel_id!,
            label: result.name,
            unsafe_subLabel: result.products?.join(', '),
          }));
      } catch {
        this.totalPages = 0;
        return [];
      }
    },
  );

  loadMoreTask = task({ drop: true }, async (): Promise<void> => {
    if (this.page + 1 >= this.totalPages) {
      return;
    }

    this.page += 1;
    const newOptions = await this.fetchPropertiesTask.perform();
    this.options = [...this.options, ...newOptions];
  });

  searchTask = task(
    { restartable: true },
    async (query: string, debounce = true): Promise<void> => {
      if (debounce) {
        await timeout();
      }

      this.searchQuery = query;
      this.page = 0;
      this.options = await this.fetchPropertiesTask.perform();
    },
  );

  get allOptions(): PropertyOption[] {
    return [this.allPropertiesOption, ...this.options];
  }

  get selectedOption(): PropertyOption {
    const value = this.args.value;
    if (!value) {
      return this.allPropertiesOption;
    }

    return this.allOptions.find((option) => option.id === value.id) ?? value;
  }

  constructor(owner: Owner, args: PropertySelectSignature['Args']) {
    super(owner, args);
    this.allPropertiesOption = {
      id: '',
      label: this.intl.t('settings.users.filter.all_properties'),
    };
  }

  @action
  handleChange(option: PropertyOption | null): void {
    this.args.onChange(option?.id ? option : null);
  }

  @action
  onOpen(): void {
    void this.searchTask.perform('', false);
  }

  @action
  onSearch(query: string): void {
    void this.searchTask.perform(query);
  }

  <template>
    <PrismComboSelectInput
      @horizontalPosition="right"
      @isSearchable={{true}}
      @isSecondary={{@isSecondary}}
      @onChange={{this.handleChange}}
      @onOpen={{this.onOpen}}
      @onSearch={{this.onSearch}}
      @options={{this.allOptions}}
      @unsafe_onLoadMore={{this.loadMoreTask.perform}}
      @value={{this.selectedOption}}
      data-test-id="property-select"
    />
  </template>
}

export type { PropertyOption };
